import { FILE_LIST_LIMIT, FILE_PLATFORM_MAX_MB } from './fileValues';
import { getFileExtension, isBlockedFileName } from '../services/attachments';

/**
 * The item form's file upload (docs/plans/data-types-file-element.md §3, §5,
 * §6; the 10b contract): what the form refuses before a byte moves, and the
 * words for every refusal — the server's own when it sent them, these when
 * the form caught the file first. One sentence per code, so a file refused
 * here and the same file refused by the server read alike.
 */

// What the server's own check calls a photo (the 10b contract: JPEG, PNG,
// WebP, GIF, AVIF, TIFF and HEIC). A PDF is never a photo (G11), and neither
// is an SVG. The server decides from the bytes; the form reads the name and
// the type the browser reported, which is enough to stop a doomed upload.
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'tif', 'tiff', 'heic', 'heif'];
const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif', 'image/tiff', 'image/heic', 'image/heif'];

/**
 * The picker's `accept` for a "Photos only" element. Never `capture`: the
 * phone then offers the camera AND the library (plan §6). `.heic` / `.heif`
 * are named because some desktop browsers do not count them as image/*.
 */
export const PHOTO_ACCEPT = 'image/*,.heic,.heif';

export function looksLikePhoto(file) {
	const mime = `${file?.type || ''}`.toLowerCase();
	if (mime && PHOTO_MIMES.includes(mime)) return true;
	return PHOTO_EXTENSIONS.includes(getFileExtension(file?.name));
}

/** The element's own limit in MB (a whole number 1–100), or null for the platform's. */
export function elementMaxMb(element) {
	const raw = element?.options?.maxSizeMb;
	return Number.isInteger(raw) && raw >= 1 && raw <= FILE_PLATFORM_MAX_MB ? raw : null;
}

const labelOf = element => element?.label?.singular || element?.name || 'This field';

/** One plain sentence per refusal code (the 10b contract's codes). */
export function fileRefusalWords(code, { element = null, limitMb = null, limit = null } = {}) {
	const label = labelOf(element);
	switch (code) {
		case 'file_wrong_kind':
			return `${label} takes photos only. A PDF or other document is not a photo.`;
		case 'file_too_large':
			return `${label} takes files up to ${limitMb || elementMaxMb(element) || FILE_PLATFORM_MAX_MB} MB.`;
		case 'too_many_files':
			return `${label} holds at most ${limit || FILE_LIST_LIMIT} files.`;
		case 'held_files_limit':
			return 'You have too many unsaved uploads. Save or cancel your other forms, then try again.';
		case 'file_not_found':
			return 'That file is no longer available. Remove it and pick it again.';
		case 'file_already_used':
			return 'That file is already in another field of this item.';
		case 'attachments_disabled':
			return 'Files are off in this collection.';
		case 'file_blocked':
			return 'That is a file type keepr does not accept.';
		default:
			return 'Unable to upload that file.';
	}
}

/** The codes a file element answers in the server's words, at the element. */
export const FILE_REFUSAL_CODES = [
	'file_wrong_kind', 'file_too_large', 'too_many_files', 'held_files_limit',
	'file_not_found', 'file_already_used', 'attachments_disabled',
];

/**
 * A refusal the server sent (an upload, or the item save), as words for the
 * element: its own message when it wrote one, else the code's sentence.
 * The API prefixes a field's message with "elements.<name>: " — dropped.
 */
export function serverFileRefusal(error, element = null) {
	const data = error?.response?.data;
	const said = typeof data?.message === 'string' ? data.message.replace(/^elements\.[^:]+:\s*/, '').trim() : '';
	// Sentence case: the API's group-level sentences start after the "elements.x:" lead.
	if (said) return `${said.charAt(0).toUpperCase()}${said.slice(1)}`;
	const status = error?.response?.status;
	const code = typeof data?.code === 'string' ? data.code
		: (status === 413 ? 'file_too_large' : (status === 415 ? 'file_wrong_kind' : null));
	return fileRefusalWords(code, { element, limitMb: data?.limitMb, limit: data?.limit });
}

/**
 * What the form refuses before uploading, mirroring the server: a blocked
 * type, a file over the element's `maxSizeMb` (or the platform's 100 MB —
 * which an attach admin is not held to; an element's own limit binds
 * everyone), a file that is not a photo under "Photos only", and a list past
 * 20. Returns `{ code, words }`, or null when the file may go.
 */
export function precheckFile(file, element, { count = 0, attachAdmin = false } = {}) {
	if (isBlockedFileName(file?.name)) {
		return { code: 'file_blocked', words: fileRefusalWords('file_blocked', { element }) };
	}
	if (element?.options?.allowMultiple === true && count >= FILE_LIST_LIMIT) {
		return { code: 'too_many_files', words: fileRefusalWords('too_many_files', { element, limit: FILE_LIST_LIMIT }) };
	}
	if (element?.options?.accept === 'image' && !looksLikePhoto(file)) {
		return { code: 'file_wrong_kind', words: fileRefusalWords('file_wrong_kind', { element }) };
	}
	const own = elementMaxMb(element);
	const limitMb = own || (attachAdmin ? null : FILE_PLATFORM_MAX_MB);
	if (limitMb && Number(file?.size) > limitMb * 1024 * 1024) {
		return { code: 'file_too_large', words: fileRefusalWords('file_too_large', { element, limitMb }) };
	}
	return null;
}
